import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

function FaceScan() {
  const navigate = useNavigate();

  const [timeLeft, setTimeLeft] = useState(30);
  const [isBreathingIn, setIsBreathingIn] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("Analyzing Your Wellness Data");

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const breatheIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setCameraError("");
        setTimeout(() => startScan(), 1000);
      }
    } catch (err) {
      console.error("Camera error:", err);
      toast.error("Unable to access camera");
      setCameraError("Camera access required. Please enable permissions.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
  };

  const clearIntervals = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (breatheIntervalRef.current) {
      clearInterval(breatheIntervalRef.current);
      breatheIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
  };

  const startScan = () => {
    if (!streamRef.current) {
      toast.error("Camera not ready");
      return;
    }

    setIsScanning(true);
    chunksRef.current = [];

    try {
      recorderRef.current = new MediaRecorder(streamRef.current, {
        mimeType: "video/webm",
      });

      recorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorderRef.current.start();
    } catch (err) {
      console.error("MediaRecorder error:", err);
      toast.error("Unable to start recording");
      return;
    }

    timerIntervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearIntervals();
          handleScanComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    breatheIntervalRef.current = setInterval(() => {
      setIsBreathingIn((prev) => !prev);
    }, 4000);
  };

  const handleScanComplete = () => {
    setIsScanning(false);

    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();

      recorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const file = new File([blob], "wellness-scan.webm", {
          type: "video/webm",
        });

        uploadScan(file);
      };
    } else {
      stopCamera();
    }
  };

  const uploadScan = async (file) => {
    setLoading(true);
    setLoadingProgress(0);
    setLoadingMessage("Uploading scan data...");

    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 30) {
          clearInterval(progressInterval);
          return 30;
        }
        return prev + Math.random() * 10;
      });
    }, 300);

    const formData = new FormData();
    formData.append("upFile", file);
    const token = localStorage.getItem("accessToken");

    try {
      const res = await axios.post(
        "https://api.healthspark.org.in/api/report/health-vitals",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      clearInterval(progressInterval);
      setLoadingProgress(40);
      
      console.log("Upload response:", res.data);

      // Backend processing me time lagta hai, so wait 3 seconds then start polling
      setLoadingMessage("Processing your vitals...");
      setTimeout(() => {
        pollForReportReady(token);
      }, 3000);

    } catch (err) {
      console.error("Upload error:", err);
      clearInterval(progressInterval);
      setLoading(false);
      toast.error("Failed to analyze wellness data. Please try again.");
      setTimeLeft(30);
      setIsBreathingIn(true);
      startCamera();
    }
  };

  const pollForReportReady = async (token, attemptCount = 0) => {
    const maxAttempts = 40; // 40 seconds max wait
    
    if (attemptCount === 0) {
      setLoadingMessage("Generating your health report...");
    }

    try {
      console.log(`Polling attempt ${attemptCount + 1}/${maxAttempts}`);
      
      // Check both APIs - vitals aur status dono
      const [vitalsRes, statusRes] = await Promise.all([
        axios.get("https://api.healthspark.org.in/api/report/health-vitals", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get("https://api.healthspark.org.in/api/report/health-vitals-status", {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);

      console.log("Vitals response:", vitalsRes.data);
      console.log("Status response:", statusRes.data);

      const progress = 40 + ((attemptCount + 1) / maxAttempts) * 55;
      setLoadingProgress(Math.min(progress, 95));

      // Check if summary exists (backend processing done)
      const hasSummary = vitalsRes?.data?.summary && vitalsRes.data.summary.trim() !== "";
      const hasVitals = statusRes?.data?.vitals?.heart_rate || statusRes?.data?.vitals?.blood_pressure;

      console.log("Has summary:", hasSummary, "Has vitals:", hasVitals);

      if (hasSummary && hasVitals) {
        console.log("✅ Report is ready!");
        setLoadingProgress(100);
        setLoadingMessage("Report ready!");
        toast.success("Health report generated successfully!");
        
        setTimeout(() => {
          setLoading(false);
          stopCamera();
          clearIntervals();
          navigate("/health-report", { replace: true });
        }, 500);
        
        return;
      }

      // If not ready and haven't exceeded max attempts, try again
      if (attemptCount < maxAttempts) {
        pollingTimeoutRef.current = setTimeout(() => {
          pollForReportReady(token, attemptCount + 1);
        }, 1000);
      } else {
        console.log("⏱️ Polling timeout - navigating anyway");
        setLoadingProgress(100);
        toast.info("Report is being generated. Please refresh if data doesn't appear.");
        setTimeout(() => {
          setLoading(false);
          stopCamera();
          clearIntervals();
          navigate("/health-report", { replace: true });
        }, 500);
      }

    } catch (err) {
      console.error("Polling error:", err);
      
      if (attemptCount < maxAttempts) {
        pollingTimeoutRef.current = setTimeout(() => {
          pollForReportReady(token, attemptCount + 1);
        }, 1000);
      } else {
        setLoading(false);
        toast.error("Could not verify report status. Please check health story page.");
        stopCamera();
        clearIntervals();
        navigate("/health-report", { replace: true });
      }
    }
  };

  const handleClose = () => {
    if (isScanning) {
      const confirmStop = window.confirm(
        "Are you sure you want to cancel the scan?"
      );
      if (confirmStop) {
        clearIntervals();
        stopCamera();
        navigate(-1);
      }
    } else {
      stopCamera();
      clearIntervals();
      navigate(-1);
    }
  };

  const formatTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
      clearIntervals();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="scan-section">
      {loading && (
        <div className="scan-loader-overlay">
          <div className="scan-loader-content">
            <div className="scan-pulse-loader">
              <div className="scan-pulse-ring"></div>
              <div className="scan-pulse-ring scan-pulse-ring-2"></div>
              <div className="scan-pulse-ring scan-pulse-ring-3"></div>
            </div>

            <h3 className="scan-loader-title">{loadingMessage}</h3>
            <p className="scan-loader-subtitle">
              Processing vitals and health signals...
            </p>

            <div className="scan-progress-container">
              <div
                className="scan-progress-fill"
                style={{ width: `${loadingProgress}%` }}
              ></div>
            </div>
            <p className="scan-progress-text">
              {Math.floor(loadingProgress)}%
            </p>
          </div>
        </div>
      )}

      <div className="scan-container">
        <header className="scan-header">
          <button className="scan-icon-btn" onClick={handleClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <main className="scan-main">
          {cameraError ? (
            <div className="scan-camera-error">
              <span className="material-symbols-outlined scan-error-icon">
                error
              </span>
              <p className="scan-error-message">{cameraError}</p>
              <button className="scan-retry-btn" onClick={startCamera}>
                Retry
              </button>
            </div>
          ) : (
            <>
              <div className="scan-face-section">
                <div className="scan-face-outline-wrapper">
                  <div className="scan-face-outline"></div>
                  <video
                    ref={videoRef}
                    className="scan-video"
                    autoPlay
                    playsInline
                    muted
                  ></video>
                </div>
                <h2 className="scan-face-title">
                  Position your face within the outline
                </h2>
              </div>

              <div className="scan-breathing-section">
                <p className="scan-breathing-text">
                  Hold still and follow the breathing guide.
                </p>
                <div className="scan-timer">{formatTime(timeLeft)}</div>

                <button
                  className={`scan-breathe-btn ${
                    isBreathingIn ? "breathe-in" : "breathe-out"
                  }`}
                >
                  {isBreathingIn ? "Breathe In" : "Breathe Out"}
                </button>
              </div>

              <p className="scan-footer-note">
                Face scan done using rPPG (remote photoplethysmography)
              </p>
            </>
          )}
        </main>
      </div>
    </section>
  );
}

export default FaceScan;